// ============================================================================
// ui/step-nav.js — WHERE "BACK" AND "FORWARD" POINT, and the pane's nav footer
// ============================================================================
// Ryan 2026-08-06: *"a permanent navigation section pinned to the bottom of the
// slide-out pane, present on every tab, on every return visit."*
//
// It replaces the "Your weight's ready — what next?" fork (logo-step's
// `askNextStep`), which was a ONE-SHOT card: it appeared once, at the end of the
// first upload, and a buyer who resumed a design or came back to Add Logo was
// never offered a way onward again. A fork that only exists at one instant is a
// route, not a control.
//
// ── ONE MODEL, TWO SURFACES ─────────────────────────────────────────────────
// Nothing here draws the phone's nav — that is `.wd-stepnav`, the ‹ prev | Title |
// next › row main.js hangs in the bottom sheet's HEADER (`setHeaderAction`), where
// it stays reachable with the sheet collapsed. The desktop footer would be invisible
// there, so the two surfaces stay separate.
//   What they must NOT have separately is an opinion about where forward GOES.
// `navTargets` is that opinion, and both call it. CLAUDE.md §0b — when two places
// need the same answer, one of them will be wrong.
//
// ── A STOP IS A STEP, OR ONE OF THE BUYER'S PICKED PRODUCTS ─────────────────
// `{ step }` — a tab. `{ step: 3, product }` — the Customize Design tab showing one
// of the weights they picked. Products are the ONLY sub-axis, because Customize
// Design is the only tab whose content changes without the tab changing.
//
// ⚠ THE SEQUENCE IS THE PICKED SET, NOT THE DENOMINATION LADDER (Ryan 2026-08-06,
// correcting the first build of this: *"the next and back buttons don't take you
// through the various weight denominations, they take you through the selected
// weight models (urethane comp bumper plate, grip plate, rubber dumbbells, etc.)"*).
// Those are two different axes on the same tab and the nav had to pick one. The
// picked set is the one a buyer is walking to REVIEW — they chose those products and
// each needs its own look — while the denomination ladder stays what it has always
// been: the Size section's own control, one click to any rung, no traversal needed.
//   It is `pickedProducts()`'s list, supplied by main.js rather than re-derived
// here, so the nav walks in exactly the order the down-tab bar cycles and the Pick
// Weights accordion lists (left-most category first, then the kind's position in its
// own drop-down). A second ordering rule would eventually disagree with the bar
// sitting two inches below these buttons.
//
// ⚠ ENTERING STEP 3 DOES NOT MOVE THE PRODUCT, and that is why this is not a flat
// list with one cursor. From Add Logo, forward is "Customize Design" — the tab, on
// whatever product is already on the stage. Only once you are STANDING on step 3
// does the picked set become the axis. A flat list would have made Add Logo's
// forward button mean "Customize Design, on the first product", silently swapping
// the plate on the way in.
// ============================================================================

import { h, replaceChildren } from './dom.js';
import { deriveHasBuilt } from './state.js';

/** How long the buyer must be idle before the forward button starts pulsing (Ryan: "5 seconds"). */
const HINT_IDLE_MS = 5000;
const HINT_RECHECK_MS = 600;  // how often to re-ask "is the forward button on screen yet?" — see `fire`
const HINT_MAX_WAITS = 50;    // × HINT_RECHECK_MS = ~30s of waiting before the nudge gives up

/** Customize Design — the only step with a sub-axis. */
export const DESIGN_STEP = 3;
/** Request Quote — the end of the line, and the subordinate link's destination. */
export const QUOTE_STEP = 4;

// The arrows. Sized in `em` so they track the label rather than being pinned to a pixel height that
// drifts the moment a font size moves. Same path and stroke weight as the layout carousel's arrows
// and the gate's, so every chevron in the tool is one drawing at different sizes.
export const CHEVRON_L =
  '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>';
export const CHEVRON_R =
  '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>';

/** Why a destination is closed. One string, so the footer's reason line and any future surface
 *  cannot describe the same lock two ways. */
export const GATE_REASON = 'Add a logo to continue';
/* The SECOND closed door on Add Logo (Ryan 2026-08-07): a logo is on file but the plate is still
   blank, because the "Who's this for?" card is still waiting on "Design My Weights". Sending someone
   to Customize Design here hands them a weight with nothing on it to customise.
   ⚠ THIS ONE IS QUIET — it does not print the footer's reason line (Ryan, same day: *"remove this
   from the 'Add Logo' tab: 'Click "Design My Weights" first'"*). It still exists, and it is still
   what the button's tooltip and accessible name say, because a greyed button that will not explain
   itself is the thing this whole `{ ok, reason }` shape was built to avoid. What changed is only
   that it stopped being PRINTED: the card it points at is already on screen with that exact button
   on it, so the caption was narrating a control the buyer is looking at.
   ⚠ AND `GATE_REASON` JOINED IT 2026-08-10 (Ryan: *"there's this 'Add a logo to continue' text under
   the next and back buttons — want that gone"*). The sentence above claimed the two were different
   because "the thing IT asks for (a logo) is not necessarily in view" — which is not true of the one
   screen this gate can fire on. It is Add Logo, whose entire body is the upload control, so this
   caption was narrating a control the buyer is looking at too. Same fault, same remedy, and the two
   are now the same shape rather than one carrying an exception.
   ⛔ QUIET IS NOT DELETED. Both still name themselves in the button's `title` and its accessible
   name — a greyed button that will not explain itself is the thing this `{ ok, reason }` shape was
   built to avoid, and it is the whole reason `quiet` is a flag and not a null reason. */
export const BUILD_REASON = 'Click "Design My Weights" first';

/**
 * May the buyer enter step `n` yet?
 *
 * The SAME fact `refreshControls` feeds `stepTabs.setDisabled` — Customize Design and Request
 * Quote are gated behind a logo so nobody wanders into an empty design step. Returned as
 * `{ ok, reason }` rather than a boolean because the two surfaces present a closed door
 * differently (the footer disables and says why; the phone pill, with half a header to work in,
 * hides), and both must be reading the same rule while doing it.
 */
export function stepGate(state, n) {
  if ((n === DESIGN_STEP || n === QUOTE_STEP) && !state.ui.hasLogo) return { ok: false, reason: GATE_REASON, quiet: true };
  /* ⚠ DESIGN ONLY, NOT QUOTE. Ryan asked for the forward button to stay greyed *"until 'Design My
     Weights' has been clicked… otherwise the weights are blank and there's no actual design on
     them"*, which is an argument about the CUSTOMIZE step specifically — that is the tab with
     nothing to work on. Request Quote is deliberately left reachable: it is the one door you never
     want to close on a buyer, and a quote for an undressed plate is still a lead. */
  /* ⚠ DERIVED FROM THE PROFILE, NOT FROM `state.ui.hasBuilt`. That flag is set live by logo-step and
     never restored, so on a resumed design it reads false — and assigning to it during restore to
     "fix" that white-screens the app, because design-panel reads the same flag to decide whether the
     size dials may seed (see `deriveHasBuilt` in ui/state.js for the full trace). Asking the design
     itself is true in both cases and owns nothing. */
  if (n === DESIGN_STEP && !deriveHasBuilt(state.designProfile)) return { ok: false, reason: BUILD_REASON, quiet: true };
  return { ok: true, reason: null };
}

/**
 * A stop describes ITSELF.
 *
 * `label` is the full destination name the footer prints and every surface speaks; `short` is what
 * the phone pill shows, where there is room for about one word. Carried on the stop rather than
 * resolved by each caller because there are now three kinds of name in play (a step, a step's
 * abbreviation, a product) and three places that would otherwise each need the whole table.
 */
function stepStop(n, { stepLabels, shortLabels }) {
  const label = stepLabels[n - 1];
  if (!label) return null;                    // off either end of the tab strip
  return { step: n, label, short: (shortLabels && shortLabels[n]) || label };
}

/**
 * One of the buyer's picks, as `pickedProducts()` describes it.
 *
 * ⚠ THE CATEGORY IS PART OF THE NAME. "Competition Urethane", "Premium Urethane" and "Round
 * Urethane" are a bumper, a grip plate and a dumbbell, and a button naming only the kind asks the
 * buyer to already know which is which. The down-tab bar prints both for exactly this reason (see
 * its `pickedProducts` note: "The down-tab shows ONE product at a time, so there's no set to read
 * it against: it has to say what it is"), and a button pointing AT a product is in the same
 * position. The short form drops the category, the same segment the bar's own <900px rule drops
 * first when it runs out of room.
 */
function productStop(p) {
  return { step: DESIGN_STEP, product: p.key, label: `${p.kind} ${p.category}`.trim(), short: p.kind };
}

/**
 * Where back and forward point from wherever the buyer is standing.
 *
 * Off step 3 → the neighbouring tabs. On step 3 → the neighbouring PICKS, falling THROUGH to a tab
 * at each end (the first product's back is Add Logo; the last one's forward is Request Quote),
 * which is what keeps the picked set from being a cul-de-sac.
 *
 * @param {object} state
 * @param {{stepLabels: string[], shortLabels?: object, products?: Array<{key,kind,category}>}} opts
 *   `products` is `pickedProducts()`'s list, in its order. See the header.
 * @returns {{back: object|null, forward: object|null}}
 */
export function navTargets(state, opts) {
  const { products = [] } = opts;
  const step = Number(state.ui.currentStep) || 1;
  if (step !== DESIGN_STEP) {
    return { back: stepStop(step - 1, opts), forward: stepStop(step + 1, opts) };
  }
  // Fewer than two picks → nothing to traverse, and the tab behaves like any other. (The down-tab
  // bar hides itself on the same test, so the two agree about when there is a set at all.)
  if (products.length < 2) return { back: stepStop(2, opts), forward: stepStop(QUOTE_STEP, opts) };

  // ⚠ NOT FOUND IS TREATED AS THE START, never as "no neighbours". `state.ui.product` can name
  // something outside the list for a beat — a pick removed from the Pick Weights tab is re-homed
  // asynchronously — and a nav that answered `{null, null}` there would strand the buyer with two
  // hidden buttons on the one tab they are most likely to be standing on.
  const at = products.findIndex((p) => p.key === state.ui.product);
  const i = at < 0 ? 0 : at;
  return {
    back: i > 0 ? productStop(products[i - 1]) : stepStop(2, opts),
    forward: i < products.length - 1 ? productStop(products[i + 1]) : stepStop(QUOTE_STEP, opts),
  };
}

/**
 * The whole phrase — "Back to Pick Weights", "Next: Round Rubber Dumbbell".
 *
 * Ryan 2026-08-06: *"Buttons name their destination."* Shared rather than formatted per surface,
 * because the desktop footer SHOWS it and the phone pill (which has room only for the short name)
 * uses it as its `aria-label` — and a screen reader hearing a different phrase from the one on
 * screen is the drift this exists to prevent.
 */
export function navPhrase(stop, dir) {
  if (!stop) return '';
  return dir < 0 ? `Back to ${stop.label}` : `Next: ${stop.label}`;
}

/**
 * THE PANE FOOTER — desktop only (main.js decides; on a phone the header pills already do this job
 * and stay visible with the sheet collapsed).
 *
 * Mounted through `sidePanel.setFooter`, which puts it in `.ds-side-panel-footer` — a surface that
 * has been styled since the Save & Share days and already carries the pane divider (`--wd-divider`,
 * the same hairline `.wd-dsection` draws between the Customize Design tab's sections, which is the
 * rule Ryan asked this to match), freezes at the open width and fades on collapse. Nothing about
 * the footer chrome is new; only its contents.
 *
 * ⚠ REQUEST QUOTE IS A TEXT LINK, NOT A THIRD BUTTON — the fork's quiet fast-lane surviving the
 * fork, and the one control here that is a jump rather than a step. It appears on Customize Design
 * and nowhere else; see the note in `sync` for why Add Logo lost it.
 *
 * @param {{state: object, targets: () => object, onGo: (stop: object) => void,
 *           forwardSlot?: (step: number) => (HTMLElement|null)}} config
 *   `targets` is main.js's own `navTargets(...)` call, passed in rather than rebuilt here — the
 *   picked set and the label tables live there, and both surfaces must ask the same question.
 *   `forwardSlot` lets a STEP put its own control where "Next" would go — see the note in `sync`.
 * @returns {{ el: HTMLElement, sync: () => void }}
 */
export function createPaneNav({ state, targets, onGo, forwardSlot }) {
  // Resolved fresh on every `sync`, and read at CLICK time rather than closed over at build time:
  // the picked set moves under this footer whenever Pick Weights is edited, and a button holding a
  // stale stop would navigate to a product that is no longer in the set.
  let now = { back: null, forward: null };

  function navBtn(dir) {
    return h('button', {
      type: 'button',
      class: 'wd-btn wd-panenav__btn wd-flat-glass wd-flat-glass--flush'
        + (dir < 0 ? ' wd-panenav__btn--back' : ' wd-panenav__btn--next'),
      onClick: () => {
        const stop = dir < 0 ? now.back : now.forward;
        if (stop) onGo(stop);
      },
    });
  }

  const backBtn = navBtn(-1);
  const nextBtn = navBtn(1);
  /* The forward HALF of the row, rather than the forward BUTTON, because the last step fills it
     with something else (see `sync`). A cell of its own means the grid column, its collapse
     animation and the solo rule all keep working without knowing what is in it. */
  const forwardCell = h('div', { class: 'wd-panenav__cell' }, nextBtn);
  const row = h('div', { class: 'wd-panenav__row' }, backBtn, forwardCell);
  // ⛔ A DISABLED BUTTON MUST SAY WHY (Ryan 2026-08-06: *"a forward button that silently does
  // nothing is worse than no button"*). `aria-live` because the line appears and disappears as the
  // gate opens — a buyer who has just been told the button is dead should hear it come alive.
  const reason = h('p', { class: 'wd-panenav__reason', role: 'status', 'aria-live': 'polite', hidden: true });
  /* ⛔ THE "Request Quote" FAST LANE IS GONE (Ryan 2026-08-07: *"on the Customize Design tab, when I
     first land on it, it says 'Request Quote' under the NEXT and BACK buttons. I thought we
     discussed getting rid of this."*).
       It had already been removed from Add Logo on 2026-08-06 and kept HERE on the argument that a
     buyer part-way through a four-product set would otherwise press Next once per remaining product
     to reach the quote. That is the one thing its removal costs, and it is the case to watch: a
     multi-product design now walks the set to get to the quote step, or uses the tab strip, which is
     always available and one click from anywhere.
       Everything it was gated by is now moot — the two hide cases (forward already IS the quote
     step; the gate is shut) went with it. */
  const el = h('nav', { class: 'wd-panenav', 'aria-label': 'Designer navigation' }, row, reason);

  /** Paint one button and report the reason it is closed (or null). */
  function paint(btn, stop, dir) {
    /* ⛔ COLLAPSED, NOT `hidden` (Ryan 2026-08-06: *"it should shrink/slide down to nothing and the
       'Next Add Logo' button should stretch/expand to take up full width"*). `hidden` is
       `display: none`, which drops the button out of the grid ENTIRELY — the column vanishes in one
       frame and there is nothing left to animate from or to. Kept in the grid and squeezed by its
       own track instead, so both halves of the move are one transition on one property (see
       `.wd-panenav__row.is-solo-*`).
         Its CONTENT is deliberately left alone on the way out — repainting a button that is on its
       way to zero width only risks a flash of the wrong label mid-collapse. */
    btn.classList.toggle('wd-panenav__btn--gone', !stop);
    if (!stop) return null;
    const gate = stepGate(state, stop.step);
    const chev = h('span', { class: 'wd-panenav__chev', 'aria-hidden': 'true', html: dir < 0 ? CHEVRON_L : CHEVRON_R });
    // TWO LINES, and the direction is the small one. A product's full name ("Competition Urethane
    // Bumper Plate") does not fit beside a chevron in half a 320px pane, and the half that must
    // survive the squeeze is the DESTINATION — direction is already said by the arrow, by the
    // button's side of the row, and by the presence of a twin opposite it. Putting "Back"/"Next"
    // on its own muted line buys the name the full width instead of sharing it with a word the
    // buyer can already infer. (The same reasoning that dropped "NEXT:" from the phone pills on
    // 2026-07-28, taken one step further because these names are longer.)
    const text = h(
      'span',
      { class: 'wd-panenav__text' },
      h('span', { class: 'wd-panenav__dir' }, dir < 0 ? 'Back' : 'Next'),
      /* The name is DOUBLE-WRAPPED: `__name` is the clipping window, `__scroll` is the ribbon that
         slides inside it. A single element cannot be both — it has to overflow to be scrollable and
         be clipped to hide the overflow. See `.wd-panenav__name` in the stylesheet. */
      h('span', { class: 'wd-panenav__name' }, h('span', { class: 'wd-panenav__scroll' }, stop.label)),
    );
    replaceChildren(btn, dir < 0 ? [chev, text] : [text, chev]);
    /* ── CONSTANT SPEED, NOT CONSTANT DURATION (Ryan 2026-08-07) ────────────────────────────────
     * *"seems to move at slightly different speeds? Might just be me… it moves slightly too slowly
     * on both."* Not just him: the travel was one fixed 2.6s whatever the distance, so a long name
     * covered more pixels in the same time than a short one. Back and Next almost never carry labels
     * of the same length, so the pair visibly disagreed.
     *   CSS cannot derive a duration from a distance, so this measures it — and now publishes the
     * DISTANCE too (`--wd-navscroll-px`). The travel used to be `min(0px, 100cqw - 100%)`, which was
     * elegant and cost more than it saved: `container-type: inline-size` makes an element's width
     * independent of its CONTENTS, so on the solo row (Pick Weights, where the button is alone and
     * centred, and `.wd-panenav__text` is shrink-to-fit) the name contributed nothing to its own
     * column — which collapsed to the width of the word "Next" above it. "Add Logo" then overflowed
     * a ~30px window and scrolled, on a label that fits with room to spare (Ryan 2026-08-07).
     *   One measurement now feeds both numbers, the container query is gone, and a label that fits
     * measures 0 overflow and therefore does not move at all.
     *   ⚠ `scrollWidth`/`clientWidth`, NOT `getBoundingClientRect()`: the ribbon may be mid-slide
     * when a sync lands, and a rect reports the TRANSFORMED box, which would feed the animation its
     * own current position and make the duration wander. These two are layout values and ignore the
     * transform entirely. */
    const nameEl = text.querySelector('.wd-panenav__name');
    const ribEl = text.querySelector('.wd-panenav__scroll');
    const overflow = Math.max(0, ribEl.scrollWidth - nameEl.clientWidth);
    /* 80px/s, landing between the two rates Ryan has now seen: the original fixed 2.6s worked out
       around 46px/s on a long name ("moves slightly too slowly"), and 110 was "a fraction too fast".
       The floor stops a few pixels of overrun reading as a flick. One number — re-pitch it here. */
    btn.style.setProperty('--wd-navscroll-px', `${overflow}px`);
    btn.style.setProperty('--wd-navscroll-ms', `${Math.max(320, Math.round((overflow / 80) * 1000))}ms`);
    /* ⛔ A STEP NAME NEVER WRAPS (Ryan 2026-08-06: *"prevent the button with 'Customize Design'
       written on it from wrapping"*). The two kinds of destination have genuinely different needs
       and one wrapping rule cannot serve both: the four STEP names are short, fixed and familiar,
       so breaking "Customize Design" over two lines only makes the tab strip's own label look
       unfamiliar — while a PRODUCT name is three or four words ("Premium Urethane Grip Plate") and
       has to wrap or it says nothing at all.
         So the KIND of stop is published to CSS rather than guessed at from length. A width-based
       rule would be the same decision made badly: it would un-wrap a short product name and wrap a
       step name the moment either string changed. */
    btn.classList.toggle('wd-panenav__btn--step', !stop.product);
    btn.classList.toggle('wd-panenav__btn--product', !!stop.product);
    // The full phrase, for the tooltip and for assistive tech — the visible name can clamp, and a
    // clamped name is exactly when a buyer wants to hover it.
    // A QUIET gate prints no reason line, so the button's own name is the only place left that can
    // say why it is dead. Hovering a greyed control is precisely the moment someone is asking.
    const phrase = navPhrase(stop, dir);
    const full = (!gate.ok && gate.quiet && gate.reason) ? `${phrase}. ${gate.reason}` : phrase;
    btn.title = full;
    btn.setAttribute('aria-label', full);
    btn.disabled = !gate.ok;
    return gate.quiet ? null : gate.reason;
  }

  function sync() {
    now = targets();
    // ⛔ BOTH, ALWAYS. These were once `paint(next) || paint(back)`, and the short-circuit meant a
    // GATED forward button (which returns a reason, i.e. truthy) skipped painting back entirely —
    // so on Add Logo with no logo yet, the back button was never drawn at all. A `||` between two
    // calls that must both happen is a bug wearing an expression's clothes.
    const step = Number(state.ui.currentStep) || 1;
    /* ── THE LAST STEP PUTS ITS OWN BUTTON IN THE FORWARD SLOT (Ryan 2026-08-07) ──────────────────
     * *"do we just replace the 'Next Request Quote' button with the 'Submit Form' button? that would
     * be clean… and we can gray out the submit button until enough info is included in the form."*
     *
     * It is, and it also settles what the quote step's footer should look like. There is nothing
     * forward of the last step, so the row would otherwise go solo and stretch "Back to Customize
     * Design" across the whole pane — which Ryan called obnoxious, and he is right: a back button
     * is not the primary action anywhere, and full width is what a primary action looks like.
     * Filling the slot with the real submit puts the strongest action of the whole tool exactly
     * where every other tab has taught the buyer to look for "what's next".
     *
     * ⚠ THE NODE IS THE STEP'S OWN BUTTON, MOVED — not a proxy that forwards a click. A proxy
     * would need its own copy of the disabled state and its own label during the send, i.e. two
     * things to keep in step with the form for no gain. Adopting the node means the gate, the
     * "Sending your request…" label and the POST are all still owned by product-step. */
    const slot = typeof forwardSlot === 'function' ? forwardSlot(step) : null;
    if (slot) { if (slot.parentNode !== forwardCell) replaceChildren(forwardCell, slot); }
    else if (nextBtn.parentNode !== forwardCell) replaceChildren(forwardCell, nextBtn);

    /* ⚠ A SWEEP, NOT A NO-OP. `paint` reuses the SAME button node for every step, so a stale
       `is-hinting` left on it by any path would ride to the next tab and glow there. Clearing it
       whenever the hint is not armed makes that impossible regardless of call order. */
    if (hintStep === null) nextBtn.classList.remove('is-hinting');
    const nextWhy = slot ? null : paint(nextBtn, now.forward, 1);
    const backWhy = paint(backBtn, now.back, -1);
    // ONE track collapses and the other takes the room. Driven off the same two facts the buttons
    // were just painted from — and a filled slot counts as "there is something forward", which is
    // what stops the quote step going solo.
    row.classList.toggle('is-solo-next', !now.back && (!!now.forward || !!slot));
    row.classList.toggle('is-solo-back', !now.forward && !slot && !!now.back);
    // Forward's reason wins: it is the direction a buyer is trying to go.
    const why = nextWhy || backWhy;
    reason.textContent = why || '';
    reason.hidden = !why;
  }

  /* ── THE IDLE NUDGE (Ryan 2026-08-07) ───────────────────────────────────────────────────────────
   * *"if the person just stays on that screen and doesn't press anything for 5 seconds, can you have
   * the 'Next Customize Design' button start pulsing blue so they know to click it to move forward?
   * This is the only time/tab this should happen on. Once they make it off this tab, if they return
   * again later, no pulse."*
   *
   * It answers a real dead end: pressing "Design My Weights" finishes the design and routes NOWHERE
   * (`finishBeat` passes a null `dest` — "stay exactly here" is deliberate), so a buyer is left on
   * Add Logo looking at a finished plate with nothing telling them the tool continues.
   *
   * ⚠ IDLE, NOT A DELAY. The timer restarts on every pointer or key event, so the pulse appears only
   * for someone who has genuinely stopped — a buyer still adjusting colours is not stuck and must
   * not be nagged. `pointerdown`/`keydown` on the ROOT, capture phase, so a press anywhere in the
   * designer counts including on controls that stop propagation.
   *
   * ⚠ ONCE PER SESSION, and `spent` is what enforces it. Ryan was explicit that returning to the tab
   * later must be silent: a hint that keeps reappearing stops reading as help and starts reading as
   * a defect. Leaving the step spends it whether or not it ever fired.
   */
  let hintTimer = null;
  let hintStep = null;   // the step the hint is armed FOR
  let spent = false;     // one-shot for the whole session
  /* Re-check budget for "armed, but the button is not on screen yet" — see `fire`. ~30s at 600ms,
     which comfortably covers a buyer admiring the plate with the sheet down before opening it, and
     still terminates rather than polling for the life of the session. */
  let hintWaits = 0;
  /* The DOCUMENT, not the nav or the app root: a buyer who is doing anything at all — including
     scrolling the storefront page the designer is embedded in — is not the stuck buyer this is for,
     and the nav has no handle on the app root to listen to instead. */
  const doc = el.ownerDocument || document;

  const stopHint = () => {
    clearTimeout(hintTimer);
    hintTimer = null;
    el.querySelector('.wd-panenav__btn--next')?.classList.remove('is-hinting');
  };
  const onActivity = () => {
    if (!hintTimer) return;
    clearTimeout(hintTimer);
    hintTimer = setTimeout(fire, HINT_IDLE_MS);
  };
  /**
   * Can the buyer actually SEE this button right now?
   *
   * ⛔ THE CASE THIS EXISTS FOR IS THE PHONE'S MINIMISED SHEET. The forward button lives in the
   * panel FOOTER, and `.ds-side-panel.is-minimized .ds-side-panel-footer` is `opacity: 0;
   * visibility: hidden`. `visibility` INHERITS, so asking the button itself is enough — no need to
   * know which ancestor hid it, which is the point: any future rule that hides the footer is caught
   * by the same test.
   */
  function canSee(node) {
    if (!node.getClientRects().length) return false;         // display:none, or detached
    return getComputedStyle(node).visibility !== 'hidden';
  }

  function fire() {
    hintTimer = null;
    const btn = el.querySelector('.wd-panenav__btn--next');
    // Re-checked at FIRE time, not just at arm time: five seconds is long enough for the buyer to
    // have moved on, and a gated button pulsing at someone is worse than no pulse at all.
    if (!btn || Number(state.ui.currentStep) !== hintStep) return;
    /* ⛔ NOT VISIBLE YET → WAIT, DO NOT SPEND IT (Ryan 2026-08-08: *"not seeing it on mobile"*).
     * This used to `return` when the button was disabled — and it also added the class blind, with
     * no test that anything could show it. On a phone the sheet is very often minimised at the 5s
     * mark (the buyer is watching the plate they just built), the footer is hidden, and `spent` was
     * already set at ARM time — so the one nudge the session gets was burned on a pulse that
     * physically could not appear, and it could never re-arm.
     *   Re-checking instead means the rule reads the way Ryan stated it: the nudge belongs to the
     * buyer who has been on this tab 5s without navigating away, and it should reach them when the
     * button comes into view — i.e. the moment they slide the sheet up.
     *   Bounded, so a buyer who parks here forever is not polled forever; `stopHint` (and therefore
     * `endForwardHint`, i.e. any navigation) clears this timer along with the original one, because
     * it is the same handle. */
    if (btn.disabled || !canSee(btn)) {
      if (hintWaits < HINT_MAX_WAITS) { hintWaits += 1; hintTimer = setTimeout(fire, HINT_RECHECK_MS); }
      return;
    }
    btn.classList.add('is-hinting');
  }

  return {
    el,
    sync,
    /** Arm the nudge for the step the buyer is standing on. No-op once spent. */
    armForwardHint() {
      if (spent) return;
      hintStep = Number(state.ui.currentStep) || null;
      if (!hintStep) return;
      spent = true;
      hintWaits = 0;   // fresh budget for this arming — `fire` spends it re-checking visibility
      clearTimeout(hintTimer);
      hintTimer = setTimeout(fire, HINT_IDLE_MS);
      /* ⚠ THIS MODULE IS DESKTOP-ONLY IN PRACTICE — main.js builds the pane footer behind
         `if (!isPhone())`, so `paneNav` is null on a phone and nothing here ever runs there. The
         phone's own nudge rides `.wd-stepnav__btn--next` in the sheet header; see `armStepNavHint`
         in main.js, which deliberately does NOT reset on activity for reasons that do not apply
         here. Keep the two in step. */
      doc.addEventListener('pointerdown', onActivity, true);
      doc.addEventListener('keydown', onActivity, true);
    },
    /**
     * End the nudge, for good. Called from `goToStep` — i.e. from ANY navigation at all.
     *
     * ⛔ UNCONDITIONAL, AND THE FIRST VERSION'S CONDITION IS THE BUG RYAN FOUND. It used to compare
     * `state.ui.currentStep` against the armed step and bail when they matched — but this runs off
     * `syncNav`, which can fire BEFORE the step value has moved. It then returned early, nothing
     * called it again, and the class stayed on the forward button. That button is a SINGLE PERSISTENT
     * element that `sync()` repaints for whatever step you are on, so the pulse followed the buyer
     * around: *"if I then click on the Pick Weights or Customize Quote tabs, both of their respective
     * 'Next' buttons are glowing too."*
     *   There is nothing to compare. A navigation is exactly the outcome the hint exists to produce,
     * so any navigation ends it — which is also the simplest possible reading of Ryan's rule: *"it's
     * a one time deal that pulses to encourage people to leave the tab, that's it."* `spent` stays
     * true, so it can never re-arm.
     */
    endForwardHint() {
      stopHint();
      hintStep = null;
      doc.removeEventListener('pointerdown', onActivity, true);
      doc.removeEventListener('keydown', onActivity, true);
    },
  };
}
